import { BuildingWithConvertedAddresses, LatLng } from '../../utils/types';
import {
  AddMyPlaceFormState,
  createInitialFormState,
} from '../../AppContent/NewLandingPage/AddMyPlaceFlow/types';
import {
  ListingAddressObject,
  ListingBuildingGeometry,
} from '../../AppContent/NewLandingPage/AddMyPlaceFlow/resolveListingBuilding';
import { SellListingLike } from '../../utils/priceEstimation';

// Building captured when the user confirms one on the wizard map. Mirrors the
// (non-exported) ClickedBuildingState in AddMyPlaceFlow so EstimateFlow can seed
// the sell flow and estimation payload without re-resolving.
export type ClickedBuildingState = {
  buildingId: string;
  addressObject: ListingAddressObject;
  geometry: ListingBuildingGeometry | null;
} | null;

// Uppercase the first letter of a single address part, skipping leading
// non-letters (house numbers, postal codes, whitespace). Copied from
// AddMyPlaceFlow so the wizard reads identically; kept local to avoid editing
// that file (jaa-303 owns it).
function capitalizeFirstLetter(part: string): string {
  for (let i = 0; i < part.length; i += 1) {
    const char = part[i];
    if (char.toLowerCase() !== char.toUpperCase()) {
      return part.slice(0, i) + char.toUpperCase() + part.slice(i + 1);
    }
  }
  return part;
}

/** Uppercase the first letter of each comma-separated address part. */
export function capitalizeAddressParts(value: string): string {
  return value.split(',').map(capitalizeFirstLetter).join(',');
}

/** Result of confirming a building on the wizard map. */
export interface BuildingSelection {
  formPatch: Partial<AddMyPlaceFormState>;
  listingLocation: LatLng | null;
  clickedBuilding: ClickedBuildingState;
}

/**
 * Translate a clicked map building into a form patch + captured building data.
 * Copied from AddMyPlaceFlow.handleSelectBuilding (jaa-91 base) so the estimate
 * wizard resolves buildings identically without importing/editing that file.
 */
export function resolveBuildingSelection(
  building: BuildingWithConvertedAddresses,
): BuildingSelection {
  const addr = building.addresses?.[0] ?? building.properties?.addresses?.[0];
  const formPatch: Partial<AddMyPlaceFormState> = {};

  if (addr) {
    const street = [addr.street, addr.houseNumber].filter(Boolean).join(' ').trim();
    formPatch.address = capitalizeAddressParts(street || building.properties?.name || '');
    formPatch.area = capitalizeAddressParts(addr.city || '');
    formPatch.postal = addr.postcode || '';
  } else if (building.properties?.name) {
    formPatch.address = capitalizeAddressParts(building.properties.name);
  }

  const listingLocation = building.centroid ?? null;
  let clickedBuilding: ClickedBuildingState = null;
  if (addr?.coordinates) {
    clickedBuilding = {
      buildingId: building.id,
      addressObject: {
        country: addr.country || '',
        region: addr.region || '',
        city: addr.city || '',
        postcode: addr.postcode || '',
        street: addr.street || '',
        houseNumber: addr.houseNumber || '',
        coordinates: addr.coordinates,
      },
      geometry: building.geometry as ListingBuildingGeometry | null,
    };
  }

  return { formPatch, listingLocation, clickedBuilding };
}

/** Address/preview strings derived from the raw form (copied from AddMyPlaceFlow). */
export interface DisplayFields {
  displayAddress: string;
  previewCity: string;
  addressWithPostal: string;
  displaySize: string;
  displayRooms: string;
}

/** Derive the display strings the shared steps expect from the raw form. */
export function deriveDisplay(form: AddMyPlaceFormState): DisplayFields {
  const addressParts = form.address.split(',').map((part) => part.trim()).filter(Boolean);
  const displayAddress = addressParts[0] || '';
  const displayArea = form.area.trim();
  const selectedAddressCity = addressParts
    .slice(1)
    .map((part) => part.replace(/^\d+\s*/, '').trim())
    .find((part) => part && !/^(finland|suomi)$/i.test(part)) || '';
  const previewCity = displayArea || selectedAddressCity;
  const displayPostal = form.postal.trim();
  const cityWithPostal = [displayPostal, previewCity].filter(Boolean).join(' ');
  const addressWithPostal = [displayAddress, cityWithPostal].filter(Boolean).join(', ');
  return {
    displayAddress,
    previewCity,
    addressWithPostal,
    displaySize: form.size.trim(),
    displayRooms: form.rooms.trim(),
  };
}

/**
 * Map wizard state to the estimation request input. Address fields prefer the
 * confirmed building's structured object; fall back to the typed form parts.
 */
export function formToEstimationListing(
  form: AddMyPlaceFormState,
  clicked: ClickedBuildingState,
  listingLocation: LatLng | null,
): SellListingLike {
  const { displayAddress, previewCity } = deriveDisplay(form);
  const ao = clicked?.addressObject;
  const coordinates = ao?.coordinates ?? listingLocation ?? undefined;
  return {
    _id: '',
    building_id: clicked?.buildingId,
    address: {
      street: ao?.street || displayAddress,
      houseNumber: ao?.houseNumber || '',
      city: ao?.city || previewCity,
      postcode: ao?.postcode || form.postal.trim(),
      country: ao?.country || 'FI',
      coordinates,
    },
    property_size: form.size.trim(),
    property_rooms: form.rooms.trim(),
    property_floor: form.floor.trim(),
    property_has_elevator: form.hasElevator ?? false,
    property_has_balcony: form.hasBalcony ?? false,
    property_renovation_status: form.renovationStatus,
    property_building_type: form.buildingType,
    property_description: form.description,
  };
}

export { createInitialFormState };
export type { AddMyPlaceFormState };
